import type { WorkflowRequestPort } from '../port';
import type { WorkflowRequest } from '../model';
import { isAmbiguousApiMutationFailure, WorkflowMutationError, workflowErrorCode } from '../workflowPageSupport';
import {
  cloneRequestApprovalSteps,
  workflowDraftResponseMatchesSnapshot,
  workflowRequestComposerFingerprint,
  type DraftSaveConflict,
  type PendingWorkflowSubmitIntent,
  type PersistedWorkflowDraft,
  type WorkflowDraftSnapshot,
} from './workflowDraftModel';

export type WorkflowDraftInput = Omit<WorkflowDraftSnapshot, 'fingerprint' | 'idempotencyKey'>;
export type WorkflowDraftPort = Pick<WorkflowRequestPort,
  'createWorkflowRequest' | 'updateWorkflowRequestDraft' | 'submitWorkflowRequest' | 'getWorkflowRequest'>;
export type WorkflowDraftSuccess = { requestId: string; draftVersion: number };
export type WorkflowDraftActionResult = ({ ok: true } & WorkflowDraftSuccess) | {
  ok: false;
  reason: unknown;
  conflict?: DraftSaveConflict;
  draftPersisted: boolean;
  ambiguousSubmit: boolean;
};

export function workflowDraftConflict(reason: unknown): DraftSaveConflict | undefined {
  const code = workflowErrorCode(reason);
  return code === 'WORKFLOW_TEMPLATE_VERSION_CONFLICT' ? 'template-version'
    : code === 'WORKFLOW_DRAFT_VERSION_CONFLICT' ? 'draft-version' : undefined;
}

// 한 작성 세션의 저장 순서·버전·멱등성을 소유한다. React, 타이머와 화면 상태는 모른다.
export class WorkflowDraftController {
  private draftID?: string;
  private draftVersion?: number;
  private queue: Promise<void> = Promise.resolve();
  private readonly idempotencyKeys = new Map<string, string>();
  private pendingSubmit?: PendingWorkflowSubmitIntent;

  constructor(private readonly options: {
    port: WorkflowDraftPort;
    initialDraft?: { id: string; draftVersion?: number };
    fixtureMode?: boolean;
    createId?: () => string;
    onPersisted?: (draft: PersistedWorkflowDraft) => void;
  }) {
    this.draftID = options.initialDraft?.id;
    this.draftVersion = options.initialDraft?.draftVersion;
  }

  get savedDraftID() { return this.draftID; }
  get hasPendingSubmit() { return Boolean(this.pendingSubmit); }
  waitForPersistence() { return this.queue; }

  snapshot(input: WorkflowDraftInput): WorkflowDraftSnapshot {
    const fingerprint = workflowRequestComposerFingerprint(input.templateId, input.title, input.values, input.approvalSteps);
    let idempotencyKey = this.idempotencyKeys.get(fingerprint);
    if (!idempotencyKey) {
      idempotencyKey = this.createId();
      this.idempotencyKeys.set(fingerprint, idempotencyKey);
    }
    return { ...input, values: structuredClone(input.values), approvalSteps: cloneRequestApprovalSteps(input.approvalSteps), fingerprint, idempotencyKey };
  }

  async checkSubmission(port = this.options.port): Promise<WorkflowRequest | undefined> {
    const pending = this.pendingSubmit;
    if (!pending) return undefined;
    const latest = await port.getWorkflowRequest(pending.draftId);
    if (latest.id !== pending.draftId) throw new Error('현재 요청과 조회한 요청이 일치하지 않습니다. 다시 확인해 주세요.');
    // 아직 같은 초안이면 늦게 끝나는 제출을 배제할 수 없으므로 동일 키 재시도만 허용한다.
    if (['DRAFT', 'NEEDS_CHANGES'].includes(latest.status) && latest.draftVersion === pending.draftVersion) return undefined;
    this.pendingSubmit = undefined;
    return latest;
  }

  persist(snapshot: WorkflowDraftSnapshot, port = this.options.port): Promise<PersistedWorkflowDraft> {
    const queued = this.queue.then(() => this.persistNext(snapshot, port));
    this.queue = queued.then(() => undefined, () => undefined);
    return queued;
  }

  async execute(
    snapshot: WorkflowDraftSnapshot,
    publish: boolean,
    onSaved: () => void,
    port = this.options.port,
  ): Promise<WorkflowDraftActionResult> {
    if (this.pendingSubmit && (!publish || this.pendingSubmit.fingerprint !== snapshot.fingerprint)) {
      return { ok: false, reason: new Error('제출 결과를 확인하기 전에는 내용을 변경하거나 저장할 수 없습니다.'), draftPersisted: true, ambiguousSubmit: true };
    }
    const pending = publish ? this.pendingSubmit : undefined;
    const retry = pending?.fingerprint === snapshot.fingerprint
      && pending.draftId === this.draftID && pending.draftVersion === this.draftVersion ? pending : undefined;
    if (!retry) this.pendingSubmit = undefined;
    let draftPersisted = Boolean(retry);
    try {
      const persisted = retry ? { id: retry.draftId, draftVersion: retry.draftVersion } : await this.persist(snapshot, port);
      if (!retry) {
        draftPersisted = true;
        onSaved();
      }
      let requestId = persisted.id;
      if (publish && !this.options.fixtureMode) {
        const intent = retry ?? {
          draftId: persisted.id,
          draftVersion: persisted.draftVersion,
          idempotencyKey: this.createId(),
          fingerprint: snapshot.fingerprint,
        };
        this.pendingSubmit = intent;
        const submitted = await port.submitWorkflowRequest(intent.draftId, intent.draftVersion, intent.idempotencyKey);
        requestId = submitted.id;
        this.pendingSubmit = undefined;
      }
      return { ok: true, requestId, draftVersion: persisted.draftVersion };
    } catch (reason) {
      const conflict = workflowDraftConflict(reason);
      const ambiguousSubmit = publish && draftPersisted && !conflict && isAmbiguousApiMutationFailure(reason);
      if (conflict || !ambiguousSubmit) this.pendingSubmit = undefined;
      return { ok: false, reason, conflict, draftPersisted, ambiguousSubmit };
    }
  }

  private createId() { return this.options.createId?.() ?? crypto.randomUUID(); }

  private async persistNext(snapshot: WorkflowDraftSnapshot, port: WorkflowDraftPort): Promise<PersistedWorkflowDraft> {
    if (this.pendingSubmit) throw new Error('제출 결과를 확인하기 전에는 초안을 저장할 수 없습니다.');
    let persisted: PersistedWorkflowDraft;
    if (this.options.fixtureMode) {
      persisted = {
        id: this.draftID ?? `fixture-draft-${snapshot.idempotencyKey}`,
        draftVersion: (this.draftVersion ?? 0) + 1,
        approvalSteps: cloneRequestApprovalSteps(snapshot.approvalSteps),
      };
    } else {
      const expectedVersion = this.draftVersion;
      if (this.draftID && expectedVersion === undefined) throw new Error('초안 버전 정보를 확인할 수 없습니다. 최신 초안을 다시 열어 주세요.');
      let request: WorkflowRequest;
      if (this.draftID) {
        request = await port.updateWorkflowRequestDraft(this.draftID, {
          title: snapshot.title, values: snapshot.values, approvalSteps: snapshot.approvalSteps, expectedVersion: expectedVersion!,
        }, snapshot.idempotencyKey);
        if (request.id !== this.draftID || request.draftVersion !== expectedVersion! + 1
          || !['DRAFT', 'NEEDS_CHANGES'].includes(request.status) || !workflowDraftResponseMatchesSnapshot(request, snapshot)) {
          throw new WorkflowMutationError(409, '저장된 초안의 버전 또는 내용이 요청과 일치하지 않습니다. 최신 초안을 다시 불러와 주세요.', 'WORKFLOW_DRAFT_VERSION_CONFLICT');
        }
      } else {
        request = await port.createWorkflowRequest({
          templateId: snapshot.templateId, templateVersionId: snapshot.templateVersionId,
          title: snapshot.title, values: snapshot.values, approvalSteps: snapshot.approvalSteps,
        }, snapshot.idempotencyKey);
      }
      if (request.draftVersion === undefined) throw new Error('저장된 초안의 버전 정보를 확인하지 못했습니다. 최신 초안을 다시 열어 주세요.');
      persisted = { id: request.id, draftVersion: request.draftVersion, approvalSteps: request.draftApprovalSteps };
    }
    this.draftID = persisted.id;
    this.draftVersion = persisted.draftVersion;
    this.idempotencyKeys.delete(snapshot.fingerprint);
    this.options.onPersisted?.(persisted);
    return persisted;
  }
}
